import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  AdmissionStepSubmitHandle,
  Step3Params,
  StudentAdmissionStepProps,
} from '../studentAdmission.interfaces';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { z } from 'zod';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TDocument,
  TDocumentOwnerType,
  TDocumentStatus,
  TDocumentType,
} from '../../../../../../../apis/documents/documents.interfaces';
import { useGradeAndFeeStructureHook } from '../../../../../institute/hooks/useGradeAndFeeStructureHook';
import { useStudentsState } from '../../../../contexts/students.context';
import { useStudentFiltersState } from '../../../../contexts/studentFilters.context';
import {
  IClassCategory,
  IClassFeeSection,
  IClassStructureSchema,
} from '../../../../../../../apis/institute/institute.interfaces';
import ControlledSelectField from '../../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledFileUploadField from '../../../../../../components/controlledComponents/ControlledFileUploadField.component';
import { getAllowedFileTypesForDocumentUpload } from '../../../../../../../utils/common';
import { useTranslation } from 'src/hooks/useTranslationHook';

export type Step3FormData = {
  grade: string;
  category: string;
  section: string;
  transferCertificate: TDocument | null;
  otherDocuments: TDocument[];
};

type AdmissionStep3Props = Omit<
  StudentAdmissionStepProps<Step3Params>,
  'onPersistStep'
> & {
  onPersistStep?: (data: Step3FormData) => Promise<void>;
};

/**
 * Admission step: grade / category gate Continue; section is required only when the selected class
 * returns sections from class-fees (same idea as Step 2 guardian schema).
 * {@link ControlledFileUploadField} writes nested paths on the parent before a full {@link TDocument}
 * exists (e.g. verificationDate only), so strict document Zod would keep the form invalid forever.
 */
const defaultValues: Step3FormData = {
  grade: '',
  category: '',
  section: '',
  transferCertificate: null,
  otherDocuments: [],
};

const AdmissionStep3GradeView = forwardRef<
  AdmissionStepSubmitHandle,
  AdmissionStep3Props
>(function AdmissionStep3GradeView(
  {
    onCompleteHandler,
    studentDetails,
    onStepValidityChange,
    onPersistStep,
  },
  ref
) {
  const { translateText } = useTranslation();
  const {
    data: gradeSchemaFromQuery,
    isLoading: gradeSchemaLoading,
    isError: gradeSchemaError,
  } = useGradeAndFeeStructureHook();
  const { gradeAndFeeStructure: gradeSchemaFromContext } = useStudentsState();
  const studentFilters = useStudentFiltersState();

  const gradeSchema = gradeSchemaFromQuery ?? gradeSchemaFromContext;
  const allowedFileTypesForDocumentUpload =
    getAllowedFileTypesForDocumentUpload();

  const admissionStep3Schema = useMemo(
    () =>
      z
        .object({
          grade: z.string().min(1, { message: 'Grade is required' }),
          category: z.string().min(1, { message: 'Category is required' }),
          section: z.string(),
          transferCertificate: z.union([
            z.null(),
            z.custom<TDocument>(() => true),
          ]),
          otherDocuments: z.array(z.custom<TDocument>(() => true)),
        })
        .superRefine((data, ctx) => {
          // BE grade-details requires a sectionId; once a class is chosen a section must be picked.
          if (data.grade?.trim() && !data.section?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: translateText('students.admission.sectionRequired'),
              path: ['section'],
            });
          }
        }),
    [translateText]
  );

  const methods = useForm<Step3FormData>({
    resolver: zodResolver(admissionStep3Schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset,
    trigger,
    getValues,
  } = methods;

  const selectedGradeId = watch('grade');

  const gradeOptions = useMemo(() => gradeSchema?.classes || [], [gradeSchema]);

  const sectionsForSelectedClass = useMemo(() => {
    const cls = gradeOptions.find(
      (item: IClassStructureSchema) => item.classId === selectedGradeId
    );
    const raw = cls?.sections ?? [];
    return raw.filter(
      (s: IClassFeeSection) =>
        Boolean(s.sectionId?.trim()) && Boolean(s.label?.trim())
    );
  }, [gradeOptions, selectedGradeId]);

  const categoriesForSelectedClass = useMemo(() => {
    const cls = gradeOptions.find(
      (item: IClassStructureSchema) => item.classId === selectedGradeId
    );
    return (cls?.categories ?? []).filter(
      (item: IClassCategory) =>
        Boolean(item) && Boolean(item.category) && Boolean(item.categoryId)
    );
  }, [gradeOptions, selectedGradeId]);

  const sectionItemList = useMemo(
    () =>
      sectionsForSelectedClass.map((s) => ({
        key: s.sectionId,
        value: s.label,
      })),
    [sectionsForSelectedClass]
  );
  const gradeItemList = useMemo(
    () =>
      gradeOptions.map((item: IClassStructureSchema) => ({
        value: item.className,
        key: item.classId,
      })),
    [gradeOptions]
  );
  const categoryItemList = useMemo(
    () =>
      categoriesForSelectedClass.map((item: IClassCategory) => ({
        value: item.category,
        key: item.categoryId,
      })),
    [categoriesForSelectedClass]
  );

  const otherDocuments = watch('otherDocuments');

  const handleAddDocument = () => {
    setValue('otherDocuments', [
      ...otherDocuments,
      {
        documentId: '',
        userId: '',
        documentType: TDocumentType.OTHER,
        documentName: '',
        submissionDate: new Date().toISOString().slice(0, 10),
        status: TDocumentStatus.PENDING,
        uploadedAt: {
          userId: '',
          timestamp: Date.now(),
        },
      },
    ]);
  };

  useEffect(() => {
    if (!selectedGradeId) {
      setValue('section', '');
      void trigger('section');
      return;
    }

    const classStructure = gradeOptions.find(
      (item: IClassStructureSchema) => item.classId === selectedGradeId
    );
    // Grade schema not loaded yet for this id — keep section (e.g. studentDetails reset).
    if (!classStructure) {
      return;
    }

    if (sectionsForSelectedClass.length === 0) {
      setValue('section', '');
      void trigger('section');
      return;
    }

    const currentSection = getValues('section');
    const isCurrentValid = sectionsForSelectedClass.some(
      (s) => s.sectionId === currentSection
    );

    if (!isCurrentValid) {
      const firstSectionId = sectionsForSelectedClass[0].sectionId;
      setValue('section', firstSectionId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      void trigger('section');
    }
  }, [
    selectedGradeId,
    gradeOptions,
    sectionsForSelectedClass,
    setValue,
    getValues,
    trigger,
  ]);

  // Keep category in sync with the chosen class: default to the first available
  // category (mirrors the section behaviour above) and clear it when no class is set.
  useEffect(() => {
    if (!selectedGradeId) {
      setValue('category', '');
      return;
    }

    const classStructure = gradeOptions.find(
      (item: IClassStructureSchema) => item.classId === selectedGradeId
    );
    // Grade schema not loaded yet for this id — keep category (e.g. studentDetails reset).
    if (!classStructure || categoriesForSelectedClass.length === 0) {
      return;
    }

    const currentCategory = getValues('category');
    const isCurrentValid = categoriesForSelectedClass.some(
      (c) => c.categoryId === currentCategory
    );

    if (!isCurrentValid) {
      setValue('category', categoriesForSelectedClass[0].categoryId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [
    selectedGradeId,
    gradeOptions,
    categoriesForSelectedClass,
    setValue,
    getValues,
  ]);

  useEffect(() => {
    if (studentDetails) {
      const transferCert =
        studentDetails.documents?.find(
          (doc) => doc.documentType === TDocumentType.TRANSFER_CERTIFICATE
        ) || null;

      const otherDocs =
        studentDetails.documents?.filter(
          (doc) => doc.documentType !== TDocumentType.TRANSFER_CERTIFICATE
        ) || [];

      reset({
        grade: studentDetails.grade || '',
        category: studentDetails.category || '',
        section: studentDetails.section || '',
        transferCertificate: transferCert,
        otherDocuments: otherDocs,
      });
    } else {
      reset(defaultValues);
    }
  }, [studentDetails, reset]);

  // New admission only: seed grade/section from the student-list filters so the
  // dialog opens on whatever class/section the user was already viewing. Runs once
  // the grade schema is available; editing an existing student keeps its own values.
  const didApplyFilterDefaultsRef = useRef(false);
  useEffect(() => {
    if (studentDetails || didApplyFilterDefaultsRef.current) {
      return;
    }
    if (gradeOptions.length === 0) {
      return;
    }

    const filterClass = gradeOptions.find(
      (item: IClassStructureSchema) =>
        item.classId === studentFilters.grade &&
        studentFilters.grade !== 'select'
    );
    // No usable grade in the filter — nothing to seed; let manual selection drive.
    if (!filterClass) {
      didApplyFilterDefaultsRef.current = true;
      return;
    }

    didApplyFilterDefaultsRef.current = true;
    setValue('grade', filterClass.classId, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const validSections = (filterClass.sections ?? []).filter(
      (s: IClassFeeSection) =>
        Boolean(s.sectionId?.trim()) && Boolean(s.label?.trim())
    );
    if (validSections.length > 0) {
      const filterSectionIsValid = validSections.some(
        (s) => s.sectionId === studentFilters.section
      );
      setValue(
        'section',
        filterSectionIsValid
          ? studentFilters.section
          : validSections[0].sectionId,
        { shouldValidate: true, shouldDirty: true }
      );
    }
  }, [
    studentDetails,
    gradeOptions,
    studentFilters.grade,
    studentFilters.section,
    setValue,
  ]);

  useEffect(() => {
    if (studentDetails && Object.keys(errors).length === 0) {
      const timeoutId = setTimeout(() => {
        onCompleteHandler({
          grade: studentDetails.grade || '',
          section: studentDetails.section || '',
          category: studentDetails.category || '',
        });
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [studentDetails, errors, onCompleteHandler]);

  useEffect(() => {
    onStepValidityChange?.(isValid);
  }, [isValid, onStepValidityChange]);

  const onSubmit = (data: Step3FormData) => {
    onCompleteHandler({
      grade: data.grade,
      section: data.section,
      category: data.category,
    });
  };

  useImperativeHandle(ref, () => ({
    submitStep: () =>
      new Promise((resolve) => {
        void handleSubmit(
          async (data) => {
            try {
              if (onPersistStep) {
                await onPersistStep(data);
              }
              onSubmit(data);
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          () => resolve(false)
        )();
      }),
  }));

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: '100%' }}
        id="admission-step-3-form"
        data-testid="admission-step-3-form"
      >
        <Grid
          container
          spacing={{ xs: 1, sm: 2 }}
          padding={{ xs: 1, sm: 2 }}
          data-testid="admission-step-3-form-container"
        >
          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                mb: { xs: 0.5, sm: 1 },
              }}
            >
              {translateText('students.admission.selectGradeAndCategory')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {translateText('students.admission.gradeCategoryHelpText')}
            </Typography>
          </Grid>
          {gradeSchemaLoading && gradeOptions.length === 0 ? (
            <Grid item xs={12} data-testid="admission-step-3-grade-schema-loading">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={20} aria-hidden />
                <Typography variant="body2" component="span">
                  {translateText('common.loading')}
                </Typography>
              </Box>
            </Grid>
          ) : null}
          {gradeSchemaError && gradeOptions.length === 0 ? (
            <Grid item xs={12} data-testid="admission-step-3-grade-schema-error">
              <Typography color="error" variant="body2">
                {translateText('common.error')}
              </Typography>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={4}>
            <ControlledSelectField
              label={translateText('students.filters.class')}
              id="grade"
              name="grade"
              isRequired
              control={control}
              errorMessage={errors.grade?.message}
              itemList={gradeItemList}
            />
          </Grid>
          {gradeOptions?.find(
            (item: IClassStructureSchema) => item.classId === selectedGradeId
          ) && (
              <Grid item xs={12} sm={4}>
                <ControlledSelectField
                  label={translateText('students.admission.category')}
                  id="category"
                  name="category"
                  isRequired
                  control={control}
                  errorMessage={errors.category?.message}
                  itemList={categoryItemList}
                />
              </Grid>
            )}
          {selectedGradeId && sectionItemList.length > 0 ? (
            <Grid
              item
              xs={12}
              sm={4}
              data-testid="admission-step-3-section-field"
            >
              <ControlledSelectField
                label={translateText('students.filters.section')}
                id="section"
                name="section"
                isRequired
                control={control}
                errorMessage={errors.section?.message}
                itemList={sectionItemList}
                dataTestId="admission-step-3-section-select"
              />
            </Grid>
          ) : null}
        </Grid>
        {watch('category') && watch('grade') ? (
          <Grid
            container
            spacing={{ xs: 1, sm: 2 }}
            padding={{ xs: 1, sm: 2 }}
            gap={{ xs: 1, sm: 2 }}
            data-testid="admission-step-3-documents-section"
          >
            {/* Transfer Certificate Upload */}
            <Grid item xs={12}>
              <ControlledFileUploadField
                id="transfer-certificate"
                sectionTitle={translateText('documents.transferCertificate')}
                label={translateText('documents.transferCertificate')}
                name="transferCertificate.documentName"
                control={control}
                errorMessage={errors.transferCertificate?.documentName?.message}
                documentOwnerType={
                  studentDetails?.id ? TDocumentOwnerType.STUDENT : undefined
                }
                documentOwnerId={studentDetails?.id}
                mergeUploadedDocumentIntoParent
              />
            </Grid>

            {/* Other Documents Upload */}
            {otherDocuments.map((_, index) => (
              <Grid item xs={12} key={index}>
                <ControlledFileUploadField
                  id={`other-document-${index}`}
                  sectionTitle={translateText(
                    'students.admission.documentTitle',
                    {
                      index: index + 1,
                    }
                  )}
                  label={translateText('students.admission.documentTitle', {
                    index: index + 1,
                  })}
                  name={`otherDocuments.${index}.documentName`}
                  control={control}
                  errorMessage={
                    errors.otherDocuments?.[index]?.documentName?.message
                  }
                  allowedFileTypes={allowedFileTypesForDocumentUpload}
                  documentOwnerType={
                    studentDetails?.id ? TDocumentOwnerType.STUDENT : undefined
                  }
                  documentOwnerId={studentDetails?.id}
                  mergeUploadedDocumentIntoParent
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Button
                variant="text"
                onClick={handleAddDocument}
                sx={{
                  mt: { xs: 1, sm: 2 },
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '16px', sm: '14px' },
                  width: { xs: '100%', sm: 'auto' },
                }}
                data-testid="add-another-document-button"
              >
                {translateText('students.admission.addAnotherDocument')}
              </Button>
            </Grid>

            {!studentDetails ? (
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
                data-testid="admission-step-3-actions"
              >
                <Button
                  type="submit"
                  onClick={() => onCompleteHandler()}
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  data-testid="admission-step-3-finish-button"
                >
                  {translateText('students.admission.completeStep3')}
                </Button>
              </Grid>
            ) : null}
          </Grid>
        ) : null}
      </form>
    </FormProvider>
  );
});

export default AdmissionStep3GradeView;
